import PropTypes from "prop-types";
import { useNavigate, useLocation } from "react-router";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { IconButton, Menu, MenuItem } from "@mui/material";
import { useCallback, useState } from "react";
// @mui
import { styled } from "@mui/material/styles";
import {
  Box,
  Stack,
  AppBar,
  Toolbar,
  Button,
  Tooltip,
  Avatar,
} from "@mui/material";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
// hooks
import useOffSetTop from "src/hooks/useOffSetTop";
import useResponsive from "src/hooks/useResponsive";
// atoms
import userAtom from "src/recoil/atoms/userAtom";
import alertAtom from "src/recoil/atoms/alertAtom";
import { freezePopUpAtom } from "src/recoil/atoms/freezePopUpAtom";
// __apis__
import { logoutRequest } from "src/__apis__/auth";
// utils
import cssStyles from "../../../utils/cssStyles";
// config
import { HEADER, NAVBAR } from "../../../config";
// components
import Logo from "../../../components/Logo";
import Iconify from "../../../components/Iconify";
import { IconButtonAnimate } from "../../../components/animate";
import useLocales from "src/hooks/useLocales";
import { Icon } from "@iconify/react";
//
// import AccountPopover from "./AccountPopover";
// import LanguagePopover from './LanguagePopover';
// import ContactsPopover from './ContactsPopover';
// import NotificationsPopover from './NotificationsPopover';
// assets
import rightLantern from "../../../assets/rightLantern.png";
import leftLantern from "../../../assets/leftLantern.png";
import ramadanKarem from "../../../assets/ramadanKarem.png";
// import eid from "../../../assets/eidMubarak.png";

// ----------------------------------------------------------------------

const RootStyle = styled(AppBar, {
  shouldForwardProp: (prop) =>
    prop !== "isCollapse" && prop !== "isOffset" && prop !== "verticalLayout",
})(({ isCollapse, isOffset, verticalLayout, theme }) => ({
  ...cssStyles(theme).bgBlur(),
  boxShadow: "none",
  height: HEADER.MOBILE_HEIGHT,
  zIndex: theme.zIndex.appBar + 1,
  transition: theme.transitions.create(["width", "height"], {
    duration: theme.transitions.duration.shorter,
  }),
  [theme.breakpoints.up("lg")]: {
    height: HEADER.DASHBOARD_DESKTOP_HEIGHT,
    width: `calc(100% - ${NAVBAR.DASHBOARD_WIDTH + 1}px)`,
    ...(isCollapse && {
      width: `calc(100% - ${NAVBAR.DASHBOARD_COLLAPSE_WIDTH}px)`,
    }),
    ...(isOffset && {
      height: HEADER.DASHBOARD_DESKTOP_OFFSET_HEIGHT,
    }),
    ...(verticalLayout && {
      width: "100%",
      height: HEADER.DASHBOARD_DESKTOP_OFFSET_HEIGHT,
      backgroundColor: theme.palette.background.default,
    }),
  },
}));

// ----------------------------------------------------------------------

DashboardHeader.propTypes = {
  onOpenSidebar: PropTypes.func,
  isCollapse: PropTypes.bool,
  verticalLayout: PropTypes.bool,
};

export default function DashboardHeader({
  onOpenSidebar,
  isCollapse = false,
  verticalLayout = false,
}) {
  const isOffset =
    useOffSetTop(HEADER.DASHBOARD_DESKTOP_HEIGHT) && !verticalLayout;

  const isDesktop = useResponsive("up", "lg");

  const navigate = useNavigate();

  const user = useRecoilValue(userAtom);

  const [loggingOut, setIsLoggingOut] = useState(false);

  const triggerFreezePopUp = useSetRecoilState(freezePopUpAtom);

  const [anchorEl, setAnchorEl] = useState(null);

  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const triggerAlert = useSetRecoilState(alertAtom);

  const onLoggingOutClick = useCallback(async () => {
    setIsLoggingOut(true);
    logoutRequest()
      .then(() => {
        navigate("/auth/signin");
        setIsLoggingOut(false);
        triggerAlert({
          triggered: true,
          type: "success",
          message: "Logged out successfully",
        });
      })
      .catch((error) => {
        triggerAlert({
          triggered: true,
          type: "error",
          message: "Error logging out, please refresh this page",
        });
      });
  }, [triggerAlert, setIsLoggingOut]);

  const { currentLang, onChangeLang, translate } = useLocales();

  const location = useLocation();

  return (
    <RootStyle
      isCollapse={isCollapse}
      isOffset={isOffset}
      verticalLayout={verticalLayout}
      style={{ backgroundColor: "transparent" }}
    >
      <Stack direction="row" justifyContent="space-between">
        <Stack direction="row" gap={2}>
          {!isDesktop && (
            <IconButtonAnimate
              onClick={onOpenSidebar}
              sx={{ color: "text.primary" }}
            >
              <Iconify icon="eva:menu-2-fill" />
            </IconButtonAnimate>
          )}
          {/* <Box
            component="img"
            src={eid}
            sx={{
              width: isDesktop ? 180 : 100,
              height: isDesktop ? 90 : 55,
              ml: isDesktop ? 3 : 0,
            }}
          /> */}
        </Stack>
        <Toolbar
          sx={{
            minHeight: "100% !important",
            px: { lg: 5 },
          }}
        >
          {isDesktop && verticalLayout && <Logo sx={{ mr: 2.5 }} />}

          <Box sx={{ flexGrow: 1 }} />

          <Stack
            direction="row"
            alignItems="center"
            spacing={{ xs: 0.5, sm: 1.5 }}
          >
            {location.pathname == "/overview/nutrition-plan" && (
              <Button
                size="large"
                variant="contained"
                onClick={() => navigate("/overview/meals-pdf")}
              >
                {translate(
                  "componentsTranslations.headerTranslations.mealsPdfButton"
                )}
                <Icon
                  icon="teenyicons:pdf-outline"
                  fontSize={20}
                  style={{
                    marginLeft: 3,
                    marginRight: currentLang.value == "ar" ? 5 : 0,
                  }}
                />
              </Button>
            )}
            {location.pathname == "/overview/workout" && (
              <Button
                size="large"
                variant="contained"
                onClick={() => navigate("/overview/exercises-pdf")}
              >
                {translate(
                  "componentsTranslations.headerTranslations.exercisesPdfButton"
                )}
                <Icon
                  icon="teenyicons:pdf-outline"
                  fontSize={20}
                  style={{
                    marginLeft: 3,
                    marginRight: currentLang.value == "ar" ? 5 : 0,
                  }}
                />
              </Button>
            )}
            {/*Language*/}
            <Tooltip title="Language">
              <IconButton
                onClick={() =>
                  onChangeLang(currentLang.value === "ar" ? "en" : "ar")
                }
              >
                <Box
                  component="img"
                  src={
                    currentLang.value === "ar"
                      ? "/flags/ic_flag_en.svg"
                      : "/flags/ic_flag_sa.svg"
                  }
                />
              </IconButton>
            </Tooltip>
            {/*Language*/}
            <Tooltip title="Account">
              <IconButton onClick={handleClick}>
                <Avatar src={user?.profile_pic} />
              </IconButton>
            </Tooltip>
            <Menu
              id="account-actions"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
            >
              <MenuItem
                onClick={() => {
                  navigate("/account/edit-profile");
                  handleClose();
                }}
              >
                Profile
              </MenuItem>
              <MenuItem
                onClick={() => {
                  triggerFreezePopUp(true);
                  handleClose();
                }}
              >
                Freeze
              </MenuItem>
            </Menu>
            <Tooltip title="Logout">
              <IconButton onClick={onLoggingOutClick}>
                <MeetingRoomIcon />
              </IconButton>
            </Tooltip>
            {/*   <LanguagePopover /> */}
            {/*  <NotificationsPopover /> */}
            {/*  <ContactsPopover /> */}
            {/*  <AccountPopover /> */}
          </Stack>
        </Toolbar>
      </Stack>
    </RootStyle>
  );
}
